/**
 * One file on its way up. Progress, then a check and the size, or Upload
 * failed with Try again. Everything is visible without hover.
 */

import { Button } from '@roger/ui-components';
import { Check, X } from 'lucide-react';

import type { PendingChip } from '../../hooks/chat/useAttachmentUpload';
import { extensionOf, formatFileSize } from '../../utils/attachments';
import { FileTypeIcon } from './FileTypeIcon';

interface PendingAttachmentChipProps {
  chip: PendingChip;
  onRemove: (localId: string) => void;
  onRetry: (localId: string) => void;
}

/** Chrome cannot decode HEIC or HEIF from an object URL. */
const NO_LOCAL_THUMBNAIL = new Set(['heic', 'heif']);

export function PendingAttachmentChip({
  chip,
  onRemove,
  onRetry,
}: PendingAttachmentChipProps) {
  return (
    <div className='flex items-center gap-2 rounded-md border px-2 py-1.5'>
      {chip.thumbnailUrl &&
      !NO_LOCAL_THUMBNAIL.has(extensionOf(chip.fileName)) ? (
        <img
          src={chip.thumbnailUrl}
          alt=''
          className='h-7 w-7 rounded object-cover'
        />
      ) : (
        <FileTypeIcon fileName={chip.fileName} className='h-7 w-7' />
      )}
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm'>{chip.fileName}</p>
        {chip.state === 'uploading' && (
          <p className='text-xs text-muted-foreground'>
            Uploading · {chip.progress}%
          </p>
        )}
        {chip.state === 'done' && (
          <p className='text-xs text-muted-foreground'>
            {formatFileSize(chip.sizeBytes)}
          </p>
        )}
        {chip.state === 'failed' && (
          <p className='text-xs text-destructive'>
            Upload failed.{' '}
            <button
              type='button'
              className='underline'
              onClick={() => onRetry(chip.localId)}
            >
              Try again
            </button>
          </p>
        )}
      </div>
      {chip.state === 'uploading' && (
        // 16 px ring; 37.7 is the circumference of r=6.
        <svg className='h-4 w-4 -rotate-90' viewBox='0 0 16 16' aria-hidden>
          <circle
            cx='8'
            cy='8'
            r='6'
            className='stroke-muted'
            fill='none'
            strokeWidth='2'
          />
          <circle
            cx='8'
            cy='8'
            r='6'
            className='stroke-primary'
            fill='none'
            strokeWidth='2'
            strokeDasharray={`${(chip.progress / 100) * 37.7} 37.7`}
          />
        </svg>
      )}
      {chip.state === 'done' && <Check className='h-4 w-4 text-green-600' />}
      <Button
        size='icon'
        variant='ghost'
        aria-label={`Remove ${chip.fileName}`}
        onClick={() => onRemove(chip.localId)}
      >
        <X className='h-4 w-4' />
      </Button>
    </div>
  );
}
